export type TranslationDownloadType = 'source' | 'target' | 'dual';

export interface TranslationDownloadUrls {
  dual_file_download_url: string;
  source_file_access_url: string;
  target_file_access_url: string;
}

export interface BuildTranslationDownloadFileNameOptions {
  sourceFileName: string;
  title?: string | null;
  targetLang: string;
  type: TranslationDownloadType;
}

export interface RunTranslationDownloadOptions
  extends BuildTranslationDownloadFileNameOptions {
  sessionId: string;
  getTranslateFileUrl: (
    sessionId: string,
    type?: TranslationDownloadType,
  ) => Promise<{ data: TranslationDownloadUrls }>;
  downloadTargetFile: (url: string) => Promise<{ data: Blob | null }>;
  downloadFile: (
    url: string,
    fileName: string,
    fetcher: () => Promise<Blob | null>,
  ) => Promise<void>;
  transformBlob?: (
    blob: Blob,
    fileName: string,
    type: TranslationDownloadType,
  ) => Blob | Promise<Blob>;
  onSuccess?: (fileName: string) => void;
}

const DOWNLOAD_TYPE_LABELS: Record<TranslationDownloadType, string> = {
  source: '源文件',
  target: '译文文件',
  dual: '整合文件',
};

function getBaseName(fileName: string) {
  const dotIndex = fileName.lastIndexOf('.');
  return dotIndex > 0 ? fileName.slice(0, dotIndex) : fileName;
}

function getExtension(fileName: string) {
  const dotIndex = fileName.lastIndexOf('.');
  return dotIndex > 0 ? fileName.slice(dotIndex + 1) : '';
}

export function buildTranslationDownloadFileName({
  sourceFileName,
  title,
  targetLang,
  type,
}: BuildTranslationDownloadFileNameOptions) {
  const extension = getExtension(sourceFileName);
  const sourceBaseName = getBaseName(sourceFileName);
  const baseName = type === 'source' ? sourceBaseName : title || sourceBaseName;
  const suffix =
    type === 'source'
      ? DOWNLOAD_TYPE_LABELS.source
      : `Suppr AI 翻译_${targetLang}_${DOWNLOAD_TYPE_LABELS[type]}`;

  return extension ? `${baseName}_${suffix}.${extension}` : `${baseName}_${suffix}`;
}

export function resolveTranslationDownloadUrl(
  urls: TranslationDownloadUrls,
  type: TranslationDownloadType,
) {
  switch (type) {
    case 'source':
      return urls.source_file_access_url;
    case 'target':
      return urls.target_file_access_url;
    case 'dual':
      return urls.dual_file_download_url;
  }
}

export async function runTranslationDownload({
  sessionId,
  sourceFileName,
  title,
  targetLang,
  type,
  getTranslateFileUrl,
  downloadTargetFile,
  downloadFile,
  transformBlob,
  onSuccess,
}: RunTranslationDownloadOptions) {
  const { data } = await getTranslateFileUrl(sessionId, type);
  const downloadUrl = resolveTranslationDownloadUrl(data, type);
  if (!downloadUrl) {
    throw new Error('DOWNLOAD_URL_EMPTY');
  }

  const fileName = buildTranslationDownloadFileName({
    sourceFileName,
    title,
    targetLang,
    type,
  });

  await downloadFile(downloadUrl, fileName, async () => {
    const { data: blob } = await downloadTargetFile(downloadUrl);
    if (!blob) return null;
    return transformBlob ? transformBlob(blob, fileName, type) : blob;
  });
  onSuccess?.(fileName);
}
